/**
 * /account/reset-password — set a new archive password.
 *
 * Two entry paths:
 *   1. User arrived via magic link → already authed → no current password
 *      required.
 *   2. Existing authed member chose "Change password" from settings →
 *      current password required.
 *
 * Posts to TCP-native /me/change-password. Authentication is fully
 * independent of any Shopify customer-account password.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Header from "../components/Header";
import LogoMark from "../components/LogoMark";
import { ArrowRight, Loader2, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
    const { user, checking, hydrateFromCustomerLogin } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromMagic = searchParams.get("from") === "magic";

    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (checking) return;
        if (!user) {
            navigate(`/account/login?next=/account/reset-password${fromMagic ? "?from=magic" : ""}`);
        }
    }, [user, checking, navigate, fromMagic]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (newPw.length < 10) { setError("Choose a password of at least 10 characters."); return; }
        if (newPw !== confirmPw) { setError("Passwords don't match."); return; }
        if (!fromMagic && !currentPw) { setError("Enter your current password to confirm the change."); return; }
        setBusy(true);
        try {
            const body = { new_password: newPw };
            if (!fromMagic) body.current_password = currentPw;
            const r = await api.post("/me/change-password", body);
            if (r.data?.token && user) {
                hydrateFromCustomerLogin(user);
            }
            setDone(true);
            toast.success("Password updated.");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) setError("Current password is incorrect.");
            else if (status === 400) setError(err?.response?.data?.detail || "Please check the new password.");
            else setError("Couldn't update password. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    if (checking || !user) {
        return <div className="min-h-screen bg-[#0A0F1A] text-[#94A3B8] flex items-center justify-center text-sm">Opening the archive…</div>;
    }

    if (done) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-white">
                <Header />
                <main className="max-w-xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
                    <div data-testid="reset-password-success">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6 inline-flex items-center gap-2">
                            <ShieldCheck className="w-3 h-3" />
                            Archive password updated
                        </div>
                        <h1 className="text-3xl sm:text-[40px] font-bold tracking-tight leading-[1.05] mb-5">
                            Done.
                        </h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-10 max-w-md">
                            Your new password is active. Use it the next time you sign in.
                        </p>
                        <Link
                            to="/dashboard"
                            data-testid="reset-password-go-dashboard"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#56CCF2] text-[#0A0F1A] text-sm font-semibold hover:bg-white"
                        >
                            Open the workspace <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0F1A] text-white">
            <Header />
            <main className="max-w-xl mx-auto px-6 sm:px-8 py-20 sm:py-28" data-testid="reset-password-form">
                <div className="mb-10">
                    <LogoMark size="sm" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6 inline-flex items-center gap-2">
                    <KeyRound className="w-3 h-3" />
                    Set a new archive password
                </div>
                <h1 className="text-3xl sm:text-[40px] font-bold tracking-tight leading-[1.05] mb-5">
                    Choose your new password.
                </h1>
                <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-10 max-w-md">
                    {fromMagic
                        ? "You're signed in via your email link. Set a new password to use for future sign-ins."
                        : "Enter your current password and choose a new one."}
                </p>

                <form onSubmit={submit} className="space-y-5">
                    {!fromMagic && (
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                                Current password
                            </label>
                            <input
                                data-testid="reset-password-current"
                                type={showPw ? "text" : "password"}
                                autoComplete="current-password"
                                value={currentPw}
                                onChange={(e) => setCurrentPw(e.target.value)}
                                required={!fromMagic}
                                className="w-full px-4 py-3 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50"
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                            New password
                        </label>
                        <div className="relative">
                            <input
                                data-testid="reset-password-new"
                                type={showPw ? "text" : "password"}
                                autoComplete="new-password"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                minLength={10}
                                maxLength={72}
                                required
                                className="w-full px-4 py-3 pr-11 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50"
                                placeholder="At least 10 characters"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#64748B] hover:text-[#94A3B8]"
                                tabIndex={-1}
                                aria-label={showPw ? "Hide password" : "Show password"}
                            >
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-medium mb-2">
                            Confirm new password
                        </label>
                        <input
                            data-testid="reset-password-confirm"
                            type={showPw ? "text" : "password"}
                            autoComplete="new-password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            minLength={10}
                            maxLength={72}
                            required
                            className="w-full px-4 py-3 rounded-md bg-[#0B1220] border border-[#1A2233] text-[15px] text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50"
                        />
                    </div>

                    {error ? (
                        <div data-testid="reset-password-error" className="text-[13px] text-[#E0B341] leading-relaxed">{error}</div>
                    ) : null}

                    <button
                        data-testid="reset-password-submit"
                        type="submit"
                        disabled={busy}
                        className="inline-flex items-center gap-2 mt-3 px-6 py-3 rounded-full bg-[#56CCF2] text-[#0B1220] text-sm font-semibold tracking-wide hover:bg-white disabled:opacity-60"
                    >
                        {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>) : (<>Update password <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                </form>
            </main>
        </div>
    );
}
